import { getSkillPackageError } from "@/api/admin/skill-packages"

/** Which skill-package call refused — the code alone cannot say. */
export type SkillPackageOperation =
  | "import"
  | "publish"
  | "delete"
  | "install"
  | "uninstall"
  | "create"
  | "update"
  | "draft"

type LocalizedError = { key: string; defaultValue: string; detail?: string }

/**
 * Locale key for a refused skill-package operation.
 *
 * Measured B1 and G1: the import-name collision and the "this package is
 * installed on an agent" delete refusal both surfaced as axios's bare
 * `"Request failed with status code 409"`. The backend sent a stable code and
 * a readable sentence for each; nothing read either.
 *
 * The backend answers BOTH with `skill_package_conflict`, so the operation
 * decides the wording — the same call-site disambiguation
 * `hosted-agent-lifecycle-panel.tsx` does for its own shared codes. The
 * server's own sentence rides along as `detail` so a refusal the UI has no
 * phrase for still says something true instead of an HTTP status.
 */
export function describeSkillPackageError(
  error: unknown,
  operation: SkillPackageOperation
): LocalizedError {
  const refusal = getSkillPackageError(error)
  if (!refusal) {
    return {
      key: "adminDashboard.skillPackages.errors.unexpected",
      defaultValue: "The operation failed. Try again.",
      detail: error instanceof Error ? error.message : undefined,
    }
  }
  const detail = refusal.serverMessage ?? undefined
  if (refusal.code === "skill_draft_unavailable") {
    return {
      key: "adminDashboard.skillPackages.errors.draftUnavailable",
      defaultValue:
        "No AI model is configured for this environment, so the assistant cannot write a draft. Fill the fields in yourself.",
      detail,
    }
  }
  if (refusal.code === "skill_packages_unavailable") {
    return {
      key: "adminDashboard.skillPackages.errors.unavailable",
      defaultValue: "The skill package service is not available in this environment.",
      detail,
    }
  }
  if (refusal.code === "skill_package_not_found") {
    return {
      key: "adminDashboard.skillPackages.errors.notFound",
      defaultValue: "The package no longer exists — reload the list.",
      detail,
    }
  }
  if (refusal.code === "skill_package_invalid") {
    // The draft assistant's own 400s (prose instead of JSON, a cap overrun)
    // arrive on this code too, and the server's sentence IS the explanation —
    // "the package was rejected as invalid" would bury it.
    if (operation === "draft") {
      return {
        key: "adminDashboard.skillPackages.errors.draftInvalid",
        defaultValue: "The assistant could not produce a usable draft — nothing was saved.",
        detail,
      }
    }
    return {
      key: "adminDashboard.skillPackages.errors.invalid",
      defaultValue: "The package was rejected as invalid.",
      detail,
    }
  }
  // skill_package_conflict — one code, several different situations.
  switch (operation) {
    case "create":
      return {
        key: "adminDashboard.skillPackages.errors.nameTaken",
        defaultValue:
          "A skill package with that name already exists. Give the import a different name.",
        detail,
      }
    case "update":
      return {
        key: "adminDashboard.skillPackages.errors.saveConflict",
        defaultValue:
          "Someone else changed the package while you were editing. Reload it and apply your changes again.",
        detail,
      }
    case "import":
      return {
        key: "adminDashboard.skillPackages.errors.nameTaken",
        defaultValue:
          "A skill package with that name already exists. Give the import a different name.",
        detail,
      }
    case "delete":
      return {
        key: "adminDashboard.skillPackages.errors.inUse",
        defaultValue:
          "The package is installed on at least one agent. Uninstall it there first, then delete it.",
        detail,
      }
    case "publish":
      return {
        key: "adminDashboard.skillPackages.errors.publishConflict",
        defaultValue: "The package changed while you were looking at it. Reload and publish again.",
        detail,
      }
    default:
      return {
        key: "adminDashboard.skillPackages.errors.conflict",
        defaultValue:
          "The change conflicts with the package's current state. Reload and try again.",
        detail,
      }
  }
}

/** Translates a warning's i18n key, falling back to the backend's (English) message. */
export type SkillWarningTranslate = (key: string, fallback: string) => string

function warningText(entry: unknown, translate?: SkillWarningTranslate): string | null {
  if (typeof entry === "string") return entry.trim() || null
  if (entry && typeof entry === "object" && "message" in entry) {
    const { message, reason } = entry as { message?: unknown; reason?: unknown }
    if (typeof message !== "string" || !message.trim()) return null
    // A known `reason` renders in the UI language, not the backend's English (#3207).
    return translate && typeof reason === "string" && /^[a-z_]+$/.test(reason)
      ? translate(`adminDashboard.hostedAgents.skillsTab.previewWarnings.${reason}`, message.trim())
      : message.trim()
  }
  return null
}

function collectWarnings(source: unknown, translate?: SkillWarningTranslate): string[] {
  if (!source || typeof source !== "object") return []
  const record = source as { warning?: unknown; warnings?: unknown }
  const entries = [record.warning, ...(Array.isArray(record.warnings) ? record.warnings : [])]
  return entries
    .map((entry) => warningText(entry, translate))
    .filter((text): text is string => text !== null)
}

/**
 * Warnings an install preview (dry run) carries, e.g. "this agent has no local
 * tools, so the package's scripts will not run". Older backends send none; the
 * backend may send a single `warning` string or a `warnings` list, at the top
 * level or on the diff. De-duplicated, empty/non-string entries dropped.
 */
export function skillInstallPreviewWarnings(
  preview: unknown,
  translate?: SkillWarningTranslate
): string[] {
  const diff =
    preview && typeof preview === "object" ? (preview as { diff?: unknown }).diff : undefined
  return [...new Set([...collectWarnings(preview, translate), ...collectWarnings(diff, translate)])]
}
